import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { ROUTE_NOVA_DATASETS } from "~/router/constants";
import { deleteDataset } from "~/services/catalog";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

interface ArchiveDatasetDialogProps {
  dataset: Pick<Dataset, "id" | "title">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ArchiveDatasetDialog = ({
  dataset,
  open,
  onOpenChange,
}: ArchiveDatasetDialogProps) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await deleteDataset(dataset.id);
      trackEvent("dataset_deleted", { dataset_id: dataset.id });
      showSuccess(`"${dataset.title}" archived`);
      navigate(ROUTE_NOVA_DATASETS, { replace: true });
    } catch (e) {
      showError(`Failed to archive dataset${isApiError(e) ? `: ${getApiErrorMessage(e)}` : ""}`);
    } finally {
      setIsArchiving(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive &ldquo;{dataset.title}&rdquo;</AlertDialogTitle>
          <AlertDialogDescription>
            Archive and hide this dataset. An admin can restore if necessary.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleArchive} disabled={isArchiving}>
            {isArchiving ? "Archiving…" : "Archive"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
